import { formatDate, toBool, toDate, tomorrow } from "rant-utils";
import { combine } from "./array";
import { getFirstObjectValue, getObjectValue } from "./objectPath";
import { dateValue, formatCurrency } from "../../formatters/format";
import { EventContext } from "../../data-entry/types";

export interface NamedScopeObject {
	name: string,
	obj: any,
}

export const CustomScriptFunctions = {

	combine,

	toDate,
	toBool,
	dateValue,
	tomorrow,
	formatDate,
	formatCurrency,

	get: (prop: string, within: any) => { 
		if (!within) return undefined;

		// try and find first occurence

		// if prop starts with a "." then want to look for an exact match within the scope
		const exact = prop.startsWith(".");
		if (exact) prop = prop.substring(1);

		// // NOTE: the last scope item will be most relevant so look there first and progressively work back to broader scope
		// for(let i=scriptContext.scope.length-1;i>=0;i--) {
		// 	const scope = scriptContext.scope[i];

			if (exact) {
				// const result = getObjectValue(scope, prop);
				const result = getObjectValue(within, prop);
				if (result) return result;

			} else {
				// search
				// const result = getFirstObjectValue(scope, prop);
				const result = getFirstObjectValue(within, prop);
				if (result) return result;

			}

		// }
		
		// didn't find anything
		return undefined;
	},

	// get the number of items in an array
	getCount: (prop: string, within: any) => {
		if (!within) return undefined;

		const v = CustomScriptFunctions.get(prop, within);
		if (Array.isArray(v)) {
			return v.length;
		} else {
			return 0;
		}
	},

	// find the count of how many array elements match a specific criteria
	getCountsOf(arrayProp: string, within: any, props: string[]) {
		if (!within) return undefined;

		const arr = CustomScriptFunctions.get(arrayProp, within);

		const counts = {} as any;
		for(let p of props) {
			counts[p] = 0;
		}

		if (Array.isArray(arr)) {
			for(let a of arr) {
				for(let p of props) {
					const condition = getFirstObjectValue(a, p);
					if (condition) counts[p]++;
				}
			}
		}

		return counts;
	},

};



function createScriptContext(
	customFunctions: any,
) {
	const context = {
		...customFunctions,
		_scope: {} as any,
	};

	return {
		run: (code: string) => {
			const contextKeys = Object.keys(context);
			const contextValues = Object.values(context);

			// create a dynamic function with the context keys as parameters
			const func = new Function(...contextKeys, code);

			// execute the dynamic function with the context values as arguments
			// i.e. each of the functions supplied are the names of all of the functions to be in scope
			return func(...contextValues);
		},

		setScope: (scope: any) => {
			context._scope = scope;
		},

	};
}


export interface ScriptEngine {
	run: (script: string, scopeForScript: any, additionalScopeItems?: (NamedScopeObject | Object)[]) => any,
}

export function createScriptEngine(options: {
	customFunctions: any,
	onCreateScopeDependentFunctions?: (scope: any) => any,
}): ScriptEngine {

	const scriptContext = createScriptContext(options.customFunctions ? options.customFunctions : {});

	let _scope = {} as any;

	function prepare(script: string, context: any) {
		// prep the scope to inject into the script

		// but first check if any scope dependent functions
		if (options.onCreateScopeDependentFunctions) {
			const extraFns = options.onCreateScopeDependentFunctions(_scope);
			for(let m in extraFns) {
				_scope[m] = extraFns[m];
			}
		}

		const locals = [];
		for(let m in _scope) {
			locals.push(m);
			//vars.push(`const ${m} = _scope["${m}"];`);
		}

		const dataLocals = [];
		if (_scope._dataWindows) {
			for(let m in _scope._dataWindows) {
				if (locals.indexOf(m) >= 0) {
					//console.error(`WARNING: double up on ${m} in both locals and script engine context`);
					continue;
				}
				dataLocals.push(m);
			}
		}

		let injectReturnStatement = false;
		const scriptTrimmed = script.trim();
		if (
			!scriptTrimmed.includes("return ") 
			&& !scriptTrimmed.includes("let ")
			&& !scriptTrimmed.includes("const ")
			&& !scriptTrimmed.includes("if ")
			// || (scriptTrimmed.startsWith("{") && scriptTrimmed.endsWith("}"))
			// || (scriptTrimmed.startsWith("(") && scriptTrimmed.endsWith(")"))
		) {
			injectReturnStatement = true;
		}

		scriptContext.setScope(_scope);

		// if (dataLocals.length === 1 && dataLocals[0] == "0") {
		// 	debugger;
		// }

		//const toExec = `(function() { //debugger;\n const { ${vars.join(", ")}} = _scope;\n\n${(injectReturnStatement ? "return " : "")}${script} })()`;
		let toExec = "";
		//toExec += "debugger;\n"; 
		if (locals.length > 0) toExec += `const { ${locals.join(", ")} } = _scope;\n`;
		if (dataLocals.length > 0) toExec += `const { ${dataLocals.join(", ")} } = _scope._dataWindows;\n`;


		const debugContext = (context && context.debugContext) ? context.debugContext.join(" -> ") : undefined;
		if (debugContext) {
			toExec += `\n//${debugContext}`;
		}

		toExec += `\n${(injectReturnStatement ? "return " : "")}${script}`;

		// if (toExec.indexOf(`return get("entity.display");`) >= 0) {
		// 	toExec = "debugger;\n" + toExec;
		// }

		return toExec;		
	}

	// NOTE: dataWindows are views into data for the current context, could be a subset of existing 
	// data or as a consequence of iterating through arrays etc
	function run(script: string, scopeForScript: any, dataWindows?: (NamedScopeObject | Object)[]) {
		
		_scope = { ...scopeForScript };
		
		if (dataWindows) {
			_scope._dataWindows = {};
			_scope._dataWindowsOrder = [];

			// if (!dataWindows.length) {
			// 	debugger;
			// }

			for(let tmp of dataWindows) {
				const s = tmp as any;

				// is it a ScopeObject?
				if (s.name && s.obj) {
					_scope._dataWindows[s.name] = s.obj;
					_scope._dataWindowsOrder.push(s.name);
				
				} else {
					// if its just any object then add all its attributes
					for(let m in s) {
						_scope._dataWindows[m] = s[m];
						_scope._dataWindowsOrder.push(m);
					}

				}
			}
		}

		let result;
		let toExec;

		try {
			toExec = prepare(script, scopeForScript);
			result = scriptContext.run(toExec);
		
		} catch (e) {
			console.error("ERROR running script: " + e);
			console.log(toExec);
			debugger;

		}

		return result;		
	}

	return {
		run,
	}

}

const scriptEngine = createScriptEngine({
	customFunctions: CustomScriptFunctions,
});

export function runScript2(script: string, scope: any, dataWindows?: (NamedScopeObject | Object)[]) {
	return scriptEngine.run(script, scope, dataWindows);
}